import { useMemo } from "react";

type BillingItem = { id: string; completed?: boolean; invoiceReportId?: string | null; customerId?: string | null; customer?: string | null; markedForInvoice?: boolean };

type CustomerOpt = { key: string; name: string };

type Params<T extends BillingItem> = {
  items: T[];
  customerListOpts: CustomerOpt[];
  billingCustomerFilter: string;
  setItems: (updater: (prev: T[]) => T[]) => void;
  setMarkedForInvoice: (id: string, checked: boolean) => Promise<void>;
  setIsMarkingAll: (val: boolean) => void;
};

export function useBillingList<T extends BillingItem>({
  items,
  customerListOpts,
  billingCustomerFilter,
  setItems,
  setMarkedForInvoice,
  setIsMarkingAll,
}: Params<T>) {
  const billingBaseItems = useMemo(
    () => items.filter(it => !!it.completed && !it.invoiceReportId),
    [items]
  );

  const billingFilteredItems = useMemo(() => {
    const nameToId = new Map(customerListOpts.map(o => [o.name.toLowerCase(), o.key]));
    const filterLower = billingCustomerFilter.trim().toLowerCase();
    const filterNameLower = billingCustomerFilter
      ? (customerListOpts.find(o => o.key === billingCustomerFilter)?.name ?? "").toLowerCase()
      : "";

    return billingBaseItems.filter((it) => {
      if (billingCustomerFilter) {
        const id = String((it as any).customerId || "");
        let effectiveId = id;

        if (!effectiveId) {
          const nm = String((it as any).customer || "").trim().toLowerCase();
          effectiveId = nameToId.get(nm) || "";
        }

        // Matcha antingen på ID eller, om filtret är ett namn, på namn
        const nameLower = String((it as any).customer || "").trim().toLowerCase();
        const matchesId = effectiveId && effectiveId === String(billingCustomerFilter);
        const matchesName =
          (!matchesId && filterLower && nameLower === filterLower) ||
          (!!filterNameLower && nameLower === filterNameLower);
        if (!matchesId && !matchesName) return false;
      }

      return true;
    });
  }, [billingBaseItems, billingCustomerFilter, customerListOpts]);

  const allFilteredMarked = useMemo(
    () => billingFilteredItems.length > 0 && billingFilteredItems.every(it => !!it.markedForInvoice),
    [billingFilteredItems]
  );

  async function toggleMarkAllInFiltered(checked: boolean) {
    setIsMarkingAll(true);
    try {
      setItems((prev: T[]) =>
        prev.map(x => billingFilteredItems.some(t => t.id === x.id) ? { ...x, markedForInvoice: checked } as T : x)
      );

      for (const it of billingFilteredItems) {
        try { await setMarkedForInvoice(it.id, checked); }
        catch { /* ignore individual errors to avoid breaking loop */ }
      }
    } finally {
      setIsMarkingAll(false);
    }
  }

  return {
    billingBaseItems,
    billingFilteredItems,
    allFilteredMarked,
    toggleMarkAllInFiltered,
  };
}
